import React from 'react';
import { Badge } from '@/components/ui/badge';
import { formatDistanceToNow } from 'date-fns';
import { LockKeyhole, Unlock, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface BotState {
  id: string;
  bot_id: string;
  current_loss_streak: number;
  mode: 'GATE' | 'OPEN';
  opened_at: string | null;
  last_exit_at: string | null;
  last_exit_win: number | null;
  locked_by_max_loss: boolean;
  updated_at: string;
  recent_results?: string;
  active_lock_pattern_id?: string | null;
  bots?: {
    name: string;
    type: string;
  };
}

interface BotStateCardProps {
  state: BotState;
  winRate?: number | null;
  totalExits?: number;
  winRateLoading?: boolean; // ✅ Phase 4: Add loading state prop
  onClick?: () => void;
  activeLockPattern?: { is_permanent: boolean; lock_pattern: string; pattern_mode?: 'NORMAL' | 'REVERSE' } | null;
  hasReversePatterns?: boolean;
}

const formatTimestamp = (timestamp: string | null) => {
  if (!timestamp) return 'Never';
  try {
    return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
  } catch {
    return 'Invalid';
  }
};

export const Type1BotCard: React.FC<BotStateCardProps> = ({ state, winRate, totalExits, winRateLoading, onClick }) => {
  const isLocked = state.locked_by_max_loss;
  const isGate = state.mode === 'GATE';
  const isOpen = state.mode === 'OPEN';
  
  const borderColor = isLocked 
    ? 'border-red-500' 
    : isGate 
    ? 'border-slate-400' 
    : 'border-green-500';
    
  const bgColor = isLocked 
    ? 'bg-red-500/10' 
    : isGate 
    ? 'bg-slate-400/10' 
    : 'bg-green-500/10';

  const hasWinRate = winRate !== null && winRate !== undefined && (totalExits || 0) > 0;

  return (
    <div 
      className={`rounded-lg border-2 p-2 transition-all duration-200 hover:scale-105 hover:shadow-lg ${borderColor} ${bgColor} ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {/* SINGLE COLUMN LAYOUT */}
      <div className="flex flex-col h-full">
        
        {/* Header with Bot Name */}
        <div className="flex items-start justify-between mb-1">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-medium truncate" title={state.bots?.name}>
              {state.bots?.name || 'Unknown'}
            </div>
          </div>
          {isLocked ? (
            <LockKeyhole className="h-3 w-3 text-red-500 flex-shrink-0 ml-1" />
          ) : isGate ? (
            <LockKeyhole className="h-3 w-3 text-slate-400 flex-shrink-0 ml-1" />
          ) : (
            <Unlock className="h-3 w-3 text-green-500 flex-shrink-0 ml-1" />
          )}
        </div>

        {/* Status Badge */}
        <div className="mb-2">
          {isLocked ? (
            <Badge variant="destructive" className="text-[8px] h-4 px-1.5">
              LOCKED
            </Badge>
          ) : (
            <Badge 
              variant={state.mode === 'OPEN' ? 'default' : 'secondary'} 
              className="text-[8px] h-4 px-1.5"
            >
              {state.mode}
            </Badge>
          )}
        </div>

        {/* Loss Streak - LARGER */}
        <div className="flex items-center justify-center flex-1">
          <span className="text-2xl font-bold font-mono">
            {state.current_loss_streak}
          </span>
        </div>

        {/* Last Exit Timestamp */}
        <div className="text-[8px] text-muted-foreground text-center mb-2">
          {formatTimestamp(state.last_exit_at)}
        </div>

        {/* WIN RATE SECTION - Ở DƯỚI CÙNG và BỰ */}
        {winRateLoading ? (
          <div className="border-t pt-2 mt-auto">
            <div className="flex justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          </div>
        ) : hasWinRate ? (
          <div className="border-t pt-2 mt-auto">
            <div className="flex flex-col items-center">
              <div className={cn(
                "text-4xl font-bold leading-none",
                winRate >= 60 ? "text-green-500" : 
                winRate >= 50 ? "text-orange-400" : 
                "text-red-500"
              )}>
                {Math.round(winRate)}
                <span className="text-lg ml-0.5">%</span>
              </div>
              <div className="text-[9px] text-muted-foreground mt-1">
                ({totalExits} exits)
              </div>
            </div>
          </div>
        ) : (
          <div className="border-t pt-2 mt-auto">
            <div className="text-[9px] text-muted-foreground text-center">
              No exits yet
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const Type2BotCard: React.FC<BotStateCardProps> = ({ state, winRate, totalExits, winRateLoading, onClick, activeLockPattern, hasReversePatterns }) => {
  const isGate = state.mode === 'GATE';
  const isOpen = state.mode === 'OPEN';
  const isPermanentLocked = isGate && activeLockPattern?.is_permanent === true;
  // Show REVERSE tag if bot has any REVERSE patterns configured OR is currently locked by a REVERSE pattern
  const isReverse = hasReversePatterns || activeLockPattern?.pattern_mode === 'REVERSE';
  
  const borderColor = isPermanentLocked 
    ? 'border-red-500 border-4' 
    : isGate 
    ? 'border-slate-400' 
    : 'border-green-500';
    
  const bgColor = isPermanentLocked
    ? 'bg-red-500/20'
    : isGate 
    ? 'bg-slate-400/10' 
    : 'bg-green-500/10';

  const hasWinRate = winRate !== null && winRate !== undefined && (totalExits || 0) > 0;

  return (
    <div 
      className={`rounded-lg border-2 p-2 transition-all duration-200 hover:scale-105 hover:shadow-lg ${borderColor} ${bgColor} ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {/* SINGLE COLUMN LAYOUT */}
      <div className="flex flex-col h-full">
        
        {/* Header with Bot Name */}
        <div className="flex items-start justify-between mb-1">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-medium truncate" title={state.bots?.name}>
              {state.bots?.name || 'Unknown'}
            </div>
          </div>
          {isPermanentLocked ? (
            <LockKeyhole className="h-4 w-4 text-red-500 flex-shrink-0 ml-1 animate-pulse" />
          ) : isOpen ? (
            <Unlock className="h-3 w-3 text-green-500 flex-shrink-0 ml-1" />
          ) : (
            <LockKeyhole className="h-3 w-3 text-slate-400 flex-shrink-0 ml-1" />
          )}
        </div>

        {/* Badges Row */}
        <div className="flex flex-wrap gap-1 mb-2">
          {/* Permanent Lock Badge */}
          {isPermanentLocked && (
            <Badge variant="destructive" className="text-[7px] h-4 px-1">
              🔒 PERMANENT
            </Badge>
          )}
          
          {/* Mode Badge */}
          <Badge 
            variant={isPermanentLocked ? 'destructive' : isOpen ? 'default' : 'secondary'} 
            className="text-[8px] h-4 px-1.5"
          >
            {isPermanentLocked ? 'LOCKED' : state.mode}
          </Badge>
          
          {/* Reverse Mode Badge */}
          {isReverse && (
            <Badge variant="outline" className="text-[7px] h-4 px-1">
              🔄 REVERSE
            </Badge>
          )}
        </div>

        {/* Recent Results Pattern - CENTERED */}
        <div className="flex flex-col items-center justify-center flex-1">
          <span className="text-[10px] font-bold font-mono tracking-tight">
            {state.recent_results || 'N/A'}
          </span>
          {isPermanentLocked && (
            <div className="text-[8px] text-red-600 font-mono mt-1">
              Pattern: {activeLockPattern.lock_pattern}
            </div>
          )}
        </div>

        {/* Time Info */}
        <div className="text-[8px] text-muted-foreground text-center mb-2">
          {isOpen 
            ? `Opened ${formatTimestamp(state.opened_at)}` 
            : `Active ${formatTimestamp(state.updated_at)}`
          }
        </div>

        {/* WIN RATE SECTION - Ở DƯỚI CÙNG và BỰ */}
        {winRateLoading ? (
          <div className="border-t pt-2 mt-auto">
            <div className="flex justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          </div>
        ) : hasWinRate ? (
          <div className="border-t pt-2 mt-auto">
            <div className="flex flex-col items-center">
              <div className={cn(
                "text-4xl font-bold leading-none",
                winRate >= 60 ? "text-green-500" : 
                winRate >= 50 ? "text-orange-400" : 
                "text-red-500"
              )}>
                {Math.round(winRate)}
                <span className="text-lg ml-0.5">%</span>
              </div>
              <div className="text-[9px] text-muted-foreground mt-1">
                ({totalExits} exits)
              </div>
            </div>
          </div>
        ) : (
          <div className="border-t pt-2 mt-auto">
            <div className="text-[9px] text-muted-foreground text-center">
              No exits yet
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
